import { AddIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
import { Box, Button, Flex, IconButton, Text } from "@chakra-ui/react";
import React from "react";
import api from "api/api";
import { invalidTextStyle } from "chakra/theme";
import DynamicTable from "components/dynamic-table";
import { openModalConfirm } from "components/modal-confirm-new";
import { useEventListener } from "services/events";
import {
  openModalUsuariosBancos,
  UsuariosBancosModal
} from "../usuarios-bancos-modal";
import { makeEvent } from "services/events";
import { Toast } from "components/toast";
import { dropdownBancos } from "../consts";
import { useApplicationContext } from "contexts/ApplicationContext";
import { InputBoxSearch } from "components/input-box-search";
import { isUserGerente } from "utils/is-gerente";

interface PageContentProps {
  usuariosBancosList: UsuarioBanco[];
  setUsuariosBancosList: React.Dispatch<React.SetStateAction<UsuarioBanco[]>>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  loadUsuariosBanco: (args: { customerId?: number }) => Promise<void>;
  empresas: any[];
  loadingEmpresas: boolean;
  onChangeEmpresa: (customerId: number) => void;
  selectedEmpresa: number | undefined;
}

export interface UsuarioBanco {
  banco: string;
  codigoDigitador?: string;
  cpfUsuarioCertificado?: string;
  customer?: string;
  id?: number | string;
  lastErrorMsg?: string;
  nomeUsuarioCertificado: null | string;
  password: string;
  username: string;
  codigoPromotora?: string;
  tokenModulo?: string;
  botIdCronowise: string;
  nomePromotora?: string;
  ativoDigitador?: boolean;
  ativoHigienizador: boolean;
  ativoHigienizadorConvenios?: boolean;
  apiKey?: string;
}

export function PageContent(props: PageContentProps) {
  const {
    usuariosBancosList,
    setUsuariosBancosList,
    loading,
    setLoading,
    loadUsuariosBanco,
    empresas,
    loadingEmpresas,
    onChangeEmpresa,
    selectedEmpresa
  } = props;
  const { user } = useApplicationContext();

  useEventListener("updateUsuarioBancosTable", () =>
    loadUsuariosBanco({ customerId: selectedEmpresa })
  );

  const acceptDeleteUsuarioBanco = async (rowData: UsuarioBanco) => {
    setLoading(true);
    try {
      await api.delete(`/usuarios-bancos/${rowData.id}`);
      setUsuariosBancosList((prev) =>
        prev.filter((curr) => curr.id !== rowData.id)
      );
    } catch (error) {
      Toast({
        title: "Erro ao excluir Usuario Banco",
        status: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteUsuarioBanco = (rowData: UsuarioBanco) => {
    openModalConfirm({
      message: `Deseja excluir ${rowData.username}${
        rowData.banco ? ` (${rowData.banco}) ` : ""
      }?`,
      onConfirm: () => acceptDeleteUsuarioBanco(rowData),
      confirmButtonStyle: { variant: "danger", leftIcon: <DeleteIcon /> }
    });
  };

  const columns = [
    {
      name: "Banco",
      render: (row: UsuarioBanco) =>
        dropdownBancos.find((crr) => crr.value === row.banco)?.name
    },
    {
      name: "Usuário",
      key: "username",
      render: (row: UsuarioBanco) => (
        <>
          <Text>{row.username}</Text>
          {row.lastErrorMsg ? (
            <Text sx={invalidTextStyle}>({row.lastErrorMsg})</Text>
          ) : null}
        </>
      )
    },
    {
      name: "Higienizador",
      render: (row: UsuarioBanco) =>
        row.ativoHigienizador ? <b>SIM</b> : "NÃO"
    },
    {
      name: "Digitador",
      render: (row: UsuarioBanco) => (row.ativoDigitador ? <b>SIM</b> : "NÃO")
    },
    {
      name: "Margem Convênios",
      render: (row: UsuarioBanco) =>
        row.ativoHigienizadorConvenios ? <b>SIM</b> : "NÃO"
    },
    {
      name: "Senha",
      key: "password",
      render: (row: UsuarioBanco) => (
        <Text>{row.password ? "*******" : ""}</Text>
      )
    },
    {
      name: "Api Key",
      key: "apiKey",
      render: (row: UsuarioBanco) => {
        const text =
          (row.apiKey || "")?.length > 10
            ? row.apiKey?.slice(0, 10) + "..."
            : row.apiKey;
        return <Text>{text}</Text>;
      }
    },
    {
      name: "Ações",
      render: (row: UsuarioBanco) => (
        <Flex gap="10px">
          <IconButton
            aria-label=""
            icon={<EditIcon />}
            isDisabled={isGerente ? !selectedEmpresa : false}
            onClick={() =>
              openModalUsuariosBancos({ row, customerId: selectedEmpresa })
            }
          />
          <IconButton
            aria-label=""
            icon={<DeleteIcon />}
            variant="danger"
            onClick={() => confirmDeleteUsuarioBanco(row)}
          />
        </Flex>
      )
    }
  ];

  const isGerente = isUserGerente(user);

  return (
    <>
      <Flex mb="10px" justifyContent="space-between">
        {isGerente &&
        user.userData.customerChildrenInheritUsersBancos !== true ? (
          <Box w="35vw" mb="16px" mr="8px">
            <Text mb="8px" fontWeight="medium">
              Empresa
            </Text>
            <InputBoxSearch
              options={empresas}
              onChange={onChangeEmpresa}
              checkIsInclude={(option) => {
                return selectedEmpresa === option.value;
              }}
              isLoading={loadingEmpresas}
              value={
                empresas.find((customer) => selectedEmpresa === customer.value)
                  ?.name
              }
              inputBoxProps={{
                bg: "#fff",
                onFocus: (e) => {
                  e.currentTarget.textContent = "";
                },
                onBlur: (e) => {
                  e.currentTarget.textContent =
                    empresas.find(
                      (customer) => selectedEmpresa === customer.value
                    )?.name || "";
                }
              }}
              openOnFocus
              closeOnSelect
            />
          </Box>
        ) : null}
        <Box>
          {isGerente &&
          user.userData.customerChildrenInheritUsersBancos !== true ? (
            <Text mb="8px">&nbsp;</Text>
          ) : null}
          {isGerente &&
          user.userData.customerChildrenInheritUsersBancos !== true ? (
            <Button
              leftIcon={<AddIcon w="13px" h="13px" />}
              onClick={() =>
                openModalUsuariosBancos({
                  customerId: selectedEmpresa || user.userData.customerId
                })
              }
            >
              Adicionar Usuário Banco
            </Button>
          ) : null}
        </Box>
      </Flex>
      <DynamicTable
        columns={columns}
        rows={usuariosBancosList}
        isLoading={loading}
        emptyMessage="Não há usuários cadastrados"
      />

      <UsuariosBancosModal />
    </>
  );
}

export const updateUsuarioBancosTable = async () => {
  await makeEvent("updateUsuarioBancosTable");
};
